import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import {
  Factory,
  FlaskConical,
  Receipt,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  Package,
  Truck,
  Beaker,
  Clock
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const { selectedCompany, companyId } = useCompany();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [materialsStats, setMaterialsStats] = useState({ today_count: 0, week_entries: 0 });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const [statsRes, ticketsRes, materialsRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/stats?company_id=${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/tickets?company_id=${companyId}&limit=5`, { credentials: 'include' }),
        fetch(`${API_URL}/api/raw-materials/recent?company_id=${companyId}&limit=5`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setRecentTickets(ticketsData.tickets || []);
      }

      if (materialsRes.ok) {
        const materialsData = await materialsRes.json();
        setRecentMaterials(materialsData.materials || []);
        setMaterialsStats({
          today_count: materialsData.today_count || 0,
          week_entries: materialsData.week_entries || 0
        });
      }

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchData();
      // Auto-refresh every 2 minutes
      const interval = setInterval(fetchData, 120000);
      return () => clearInterval(interval);
    }
  }, [authLoading, user, companyId, fetchData]);

  const getChartData = () => {
    if (!stats?.daily_production) return [];
    
    return Object.entries(stats.daily_production)
      .map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        count
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getStatusBadge = (status, labStatus) => {
    if (status === 'closed' && labStatus === 'rejected') {
      return <span className="status-badge status-rejected">Rejected</span>;
    }
    
    const statusMap = {
      pending_lab: { class: 'status-pending-lab', label: 'Pending Lab' },
      pending_accounts: { class: 'status-pending-accounts', label: 'Pending Accounts' },
      closed: { class: 'status-closed', label: 'Closed' }
    };
    
    const statusInfo = statusMap[status] || { class: 'status-pending-lab', label: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-500 mt-1">Welcome back, {user?.name?.split(' ')[0]}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <button
              onClick={fetchData}
              className="action-btn action-btn-secondary gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-grid" data-testid="kpi-cards">
          <div 
            className="kpi-card cursor-pointer hover:shadow-md transition-shadow" 
            data-testid="kpi-today-production"
            onClick={() => navigate(`/tickets?start_date=${new Date().toISOString().split('T')[0]}&end_date=${new Date().toISOString().split('T')[0]}`)}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Factory className="w-5 h-5 text-slate-700" />
              </div>
              <span className="kpi-label">Today's Production</span>
            </div>
            <div className="kpi-value">{stats?.today_production || 0}</div>
            <div className="kpi-subtext">Batches entered today</div>
          </div>

          <div 
            className="kpi-card warning cursor-pointer hover:shadow-md transition-shadow" 
            data-testid="kpi-pending-lab"
            onClick={() => navigate('/tickets?status=pending_lab')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-amber-600" />
              </div>
              <span className="kpi-label">Pending Lab</span>
            </div>
            <div className="kpi-value">{stats?.pending_lab || 0}</div>
            <div className="kpi-subtext">Awaiting lab approval</div>
          </div>

          <div 
            className="kpi-card info cursor-pointer hover:shadow-md transition-shadow" 
            data-testid="kpi-pending-accounts"
            onClick={() => navigate('/tickets?status=pending_accounts')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <span className="kpi-label">Pending Stock Entry</span>
            </div>
            <div className="kpi-value">{stats?.pending_accounts || 0}</div>
            <div className="kpi-subtext">Awaiting Tally entry</div>
          </div>

          <div 
            className="kpi-card success cursor-pointer hover:shadow-md transition-shadow" 
            data-testid="kpi-closed"
            onClick={() => navigate('/tickets?status=closed')}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="kpi-label">Closed</span>
            </div>
            <div className="kpi-value">{stats?.total_closed || 0}</div>
            <div className="kpi-subtext">Completed tickets</div>
          </div>

          {(user?.role === 'lab' || user?.role === 'manager') && (
            <div 
              className="kpi-card error cursor-pointer hover:shadow-md transition-shadow" 
              data-testid="kpi-rejected"
              onClick={() => navigate('/tickets?status=closed')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <span className="kpi-label">Lab Rejected</span>
              </div>
              <div className="kpi-value">{stats?.lab_rejected || 0}</div>
              <div className="kpi-subtext">Total rejected batches</div>
            </div>
          )}

          {/* Raw Materials KPI */}
          <div 
            className="kpi-card cursor-pointer hover:shadow-md transition-shadow" 
            data-testid="kpi-raw-materials"
            onClick={() => navigate('/raw-materials')}
            style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#bbf7d0' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <span className="kpi-label">Raw Materials Today</span>
            </div>
            <div className="kpi-value">{materialsStats.today_count}</div>
            <div className="kpi-subtext">{materialsStats.week_entries} entries this week</div>
          </div>

          {/* Supplier Samples KPI */}
          {(user?.effective_permissions?.includes('supplier_samples') || user?.role === 'manager' || user?.role === 'lab') && (
            <div 
              className="kpi-card warning cursor-pointer hover:shadow-md transition-shadow" 
              data-testid="kpi-pending-samples"
              onClick={() => navigate('/supplier-samples?status=Pending')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-amber-600" />
                </div>
                <span className="kpi-label">Samples Pending Test</span>
              </div>
              <div className="kpi-value">{stats?.pending_samples || 0}</div>
              <div className="kpi-subtext">
                {stats?.approved_samples || 0} approved, {stats?.rejected_samples || 0} rejected
              </div>
            </div>
          )}
        </div>

        {/* Recent Raw Materials Section */}
        {recentMaterials.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-8" data-testid="recent-materials">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Recent Raw Materials</h2>
                  <p className="text-sm text-slate-500">Latest materials received at factory</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/raw-materials')}
                className="text-sm font-medium text-green-600 hover:text-green-700"
                data-testid="view-all-materials-btn"
              >
                View All →
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentMaterials.slice(0, 6).map((mat) => (
                <div 
                  key={mat.material_id} 
                  className="bg-white rounded-lg border border-green-100 p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-slate-900 text-sm">{mat.material_name}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(mat.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-green-600">{mat.quantity} {mat.unit}</span>
                    {mat.supplier && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{mat.supplier}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Added by {mat.created_by_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chart Section */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8" data-testid="production-chart">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-5 h-5 text-slate-700" />
            <h2 className="text-lg font-semibold text-slate-900">Production Trend (Last 7 Days)</h2>
          </div>
          <div style={{ width: '100%', height: '256px', minWidth: '300px' }}>
            <ResponsiveContainer width="100%" height={256}>
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: '#64748B' }}
                  tickLine={{ stroke: '#E2E8F0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748B' }}
                  tickLine={{ stroke: '#E2E8F0' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    background: '#fff', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#0F172A" 
                  radius={[4, 4, 0, 0]}
                  name="Batches"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Tickets */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="recent-tickets">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Recent Tickets</h2>
            <button
              onClick={() => navigate('/tickets')}
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
              data-testid="view-all-tickets-btn"
            >
              View All →
            </button>
          </div>

          {/* Desktop Table */}
          <div className="table-responsive hidden sm:block">
            <table className="w-full">
              <thead>
                <tr className="table-header-row">
                  <th className="table-header-cell">Ticket ID</th>
                  <th className="table-header-cell">Batch Number</th>
                  <th className="table-header-cell">Product</th>
                  <th className="table-header-cell">Quantity</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.map((ticket) => (
                  <tr 
                    key={ticket.ticket_id} 
                    className="table-row cursor-pointer"
                    onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                    data-testid={`ticket-row-${ticket.ticket_id}`}
                  >
                    <td className="table-cell font-mono font-medium">{ticket.ticket_id}</td>
                    <td className="table-cell">{ticket.batch_number}</td>
                    <td className="table-cell">{ticket.product_name}</td>
                    <td className="table-cell">{ticket.quantity} {ticket.unit}</td>
                    <td className="table-cell">{getStatusBadge(ticket.status, ticket.lab_status)}</td>
                    <td className="table-cell">{new Date(ticket.production_date).toLocaleDateString()}</td>
                  </tr>
                ))}
                {recentTickets.length === 0 && (
                  <tr>
                    <td colSpan={6} className="table-cell text-center text-slate-500 py-8">
                      No tickets found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="card-list-mobile p-4 space-y-3 sm:hidden">
            {recentTickets.map((ticket) => (
              <div
                key={ticket.ticket_id}
                className="ticket-card-mobile cursor-pointer"
                onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                data-testid={`ticket-card-${ticket.ticket_id}`}
              >
                <div className="ticket-card-header">
                  <span className="ticket-card-id">{ticket.ticket_id}</span>
                  {getStatusBadge(ticket.status, ticket.lab_status)}
                </div>
                <div className="ticket-card-body">
                  <span className="ticket-card-label">Batch:</span>
                  <span className="ticket-card-value">{ticket.batch_number}</span>
                  <span className="ticket-card-label">Product:</span>
                  <span className="ticket-card-value">{ticket.product_name}</span>
                  <span className="ticket-card-label">Quantity:</span>
                  <span className="ticket-card-value">{ticket.quantity} {ticket.unit}</span>
                  <span className="ticket-card-label">Date:</span>
                  <span className="ticket-card-value">{new Date(ticket.production_date).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {recentTickets.length === 0 && (
              <div className="text-center text-slate-500 py-8">
                No tickets found
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
